/**
 * Shared `origin:*` label vocabulary for agent-opened PRs
 * (fix-delegation parity — codedrifters/packages#859).
 *
 * The `pr-reviewer` decides whether it may autonomously delegate
 * in-scope fixes to the `issue-worker` by looking at the PR's origin
 * label. Historically only {@link ORIGIN_ISSUE_WORKER_LABEL} existed, so
 * every PR opened by the phased analyst/writer family looked
 * human-authored to the reviewer and had its delegation hand-off
 * skipped — those PRs waited for a human instead of self-healing.
 *
 * Stamping the literal `origin:issue-worker` label on an analyst PR
 * would be semantically false (the glossary defines it as "PR was opened
 * by the `issue-worker` agent"), so the analyst family stamps the
 * {@link ORIGIN_AGENT_LABEL} umbrella label instead and both labels
 * resolve to delegation-eligible.
 *
 * Scope note: this is **fix-delegation parity only**. Sweep and
 * auto-merge eligibility have never been origin-gated (`cmd_review` has
 * no origin/author check), so nothing here changes which PRs merge.
 *
 * The stamp section is rendered identically into every phased
 * analyst/writer prompt via {@link renderOriginStampSection} so the
 * protocol never drifts between bundles — the same pattern the
 * claim-verification and project-context sections use.
 */

/**
 * Origin label stamped by the `issue-worker` agent on the PRs it opens.
 * Unchanged by #859 — the issue-worker keeps its own specific label.
 */
export const ORIGIN_ISSUE_WORKER_LABEL = "origin:issue-worker";

/**
 * Umbrella origin label stamped by every phased analyst/writer agent on
 * the PRs it opens. Semantically "opened by an agent in this project's
 * pipeline", as distinct from the narrower `origin:issue-worker`.
 */
export const ORIGIN_AGENT_LABEL = "origin:agent";

/**
 * Every origin label that resolves to **fix-delegation-eligible** in the
 * `pr-reviewer`'s invocation guard. A PR carrying any of these is
 * bot-authored and may have in-scope fixes delegated autonomously,
 * without the `--allow-human-author` opt-in flag.
 */
export const DELEGATION_ELIGIBLE_ORIGIN_LABELS: ReadonlyArray<string> = [
  ORIGIN_ISSUE_WORKER_LABEL,
  ORIGIN_AGENT_LABEL,
];

/**
 * Inline prose fragment naming both delegation-eligible origin labels.
 * Used wherever a prompt previously said "the `origin:issue-worker`
 * label" while meaning "an origin label that marks the PR bot-authored".
 */
export const DELEGATION_ELIGIBLE_ORIGIN_PHRASE = `an origin label (\`${ORIGIN_ISSUE_WORKER_LABEL}\` or \`${ORIGIN_AGENT_LABEL}\`)`;

/**
 * Bare "either label" fragment, for prose that already sits inside
 * parentheses (where {@link DELEGATION_ELIGIBLE_ORIGIN_PHRASE} would
 * nest a second pair).
 */
export const DELEGATION_ELIGIBLE_ORIGIN_LIST = `\`${ORIGIN_ISSUE_WORKER_LABEL}\` or \`${ORIGIN_AGENT_LABEL}\``;

/**
 * Render the shared `## Origin label` section as prompt lines. Spread
 * into a phased analyst/writer sub-agent `prompt` array with
 * `...renderOriginStampSection()`.
 */
export function renderOriginStampSection(): ReadonlyArray<string> {
  return [
    "## Origin label",
    "",
    `Every PR you open must carry the \`${ORIGIN_AGENT_LABEL}\` label. It is`,
    "the umbrella origin label for **agent-opened** PRs: the `pr-reviewer`",
    "reads it to decide whether it may autonomously delegate in-scope",
    "review fixes back to the `issue-worker`. A PR that reaches the",
    "reviewer with no origin label looks human-authored, so the reviewer",
    "skips the delegation hand-off and the PR sits waiting for a human",
    "instead of being self-healed by the pipeline.",
    "",
    "Pass the label on the `gh pr create` invocation:",
    "",
    "```bash",
    'gh pr create --title "<type>(<scope>): <description>" \\',
    `  --label '${ORIGIN_AGENT_LABEL}' \\`,
    '  --body "<summary>',
    "",
    'Closes #<issue-number>"',
    "```",
    "",
    "If your project's PR workflow opens the PR for you and the label is",
    "missing afterwards, add it immediately — before handing the PR to",
    "review:",
    "",
    "```bash",
    `gh pr edit <pr-number> --add-label '${ORIGIN_AGENT_LABEL}'`,
    "```",
    "",
    `Stamp \`${ORIGIN_AGENT_LABEL}\`, never \`${ORIGIN_ISSUE_WORKER_LABEL}\` —`,
    `\`${ORIGIN_ISSUE_WORKER_LABEL}\` means the PR was opened by the`,
    "`issue-worker` agent specifically, and claiming it on an analyst PR",
    "would be false. Both labels are delegation-eligible, so the umbrella",
    "label buys the same autonomous fix hand-off without the false claim.",
    "",
    `The \`${ORIGIN_AGENT_LABEL}\` label ships with the \`pr-review\` bundle's`,
    "label set, so it is already present in the repository's label",
    "registry — you never need to create it.",
  ];
}
